import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiLink, FiRefreshCw } from 'react-icons/fi'
import { ResponsiveModal, StatusChip, type StatusVariant } from '../../shared'
import { BillContentDetails } from '../BillContentDetails'
import { BillLinkModal } from '../BillLinkModal'
import { LegiScanCredit } from '../LegiScanCredit'
import { useFillVotes } from '../../../hooks/useLobbyReconciliation'
import { useIsMobile } from '../../../hooks/useIsMobile'
import type { AskBill, AskReconciliationDto, LegislatorVote, SentimentDisposition } from '../../../types/lobbyDay'
import { LegislatorVoteRow } from './LegislatorVoteRow'
import { FillResultSummary, missReasonLabel } from './FillResultSummary'

export interface AskSectionProps {
  eventId: string
  ask: AskReconciliationDto
  onRowChange: (askId: string, legislatorId: string, vote: LegislatorVote, override: SentimentDisposition | null, notes: string | null) => void
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Map a LegiScan status label onto the app's status-chip vocabulary (spec §6.1). The labels are
// LegiScan's free text (e.g. "Passed House", "In committee"), so match on keyword.
function billStatusVariant(statusLabel: string | null): StatusVariant {
  const s = (statusLabel ?? '').toLowerCase()
  if (/pass|enroll|signed|chaptered|enacted|adopt|approved/.test(s)) return 'success'
  if (/fail|veto|dead|tabled|stricken|defeat|withdrawn/.test(s)) return 'danger'
  if (/committee|introduc|refer|reading|filed|prefiled|pending|engross/.test(s)) return 'info'
  return 'neutral'
}

function billChipText(bill: AskBill): string {
  const date = formatDate(bill.statusDate)
  return date ? `${bill.statusLabel} · ${date}` : (bill.statusLabel ?? '')
}

export function AskReconciliationSection({ eventId, ask, onRowChange }: AskSectionProps) {
  const isMobile = useIsMobile()
  const { fill, result, isPending } = useFillVotes(eventId, ask.askId)
  const [showResult, setShowResult] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const qc = useQueryClient()

  // useFillVotes surfaces `result` only on the poll's terminal Succeeded branch, and mints a NEW
  // object each fill -- so opening on every truthy transition reopens on a re-run too. Closing the
  // modal (showResult=false) won't reopen until the next fill produces a fresh result reference.
  useEffect(() => {
    if (result) setShowResult(true)
  }, [result])

  // The last fill's unresolved reasons, keyed by the legislator display name (identical to the
  // backend's `${FirstName} ${LastName}`.Trim()), humanized once here for the per-row hint.
  const missByName = useMemo(() => {
    const m = new Map<string, string>()
    for (const miss of result?.noVoteFound ?? []) m.set(miss.legislatorName, missReasonLabel(miss.reason))
    return m
  }, [result])

  const refreshedDates = ask.linkedBills
    .map((b) => b.lastRefreshedAt)
    .filter((d): d is string => !!d)
    .sort()
  const lastRefreshed = refreshedDates.length > 0 ? refreshedDates[refreshedDates.length - 1] : null

  // LW-23: derive the roll-call zone's state from the DTO alone (no backend change) -- 'none'
  // when nothing is linked yet, else 'ingested' once at least one row carries a LegiScan-sourced
  // vote, else 'awaiting' (linked but not yet filled/synced).
  const scorecard = ask.legislators
  const ingestedCount = scorecard.filter((r) => r.voteSource === 'LegiScan').length
  const isIngested = ingestedCount > 0
  const rollCallState: 'none' | 'awaiting' | 'ingested' = ask.linkedBills.length === 0
    ? 'none'
    : isIngested ? 'ingested' : 'awaiting'

  const fillStatusText = result
    ? `${result.filled} filled · ${result.keptManual} kept manual · ${result.noVoteFound.length} no vote found`
    : rollCallState === 'ingested'
      ? `Roll call ingested · ${ingestedCount} of ${scorecard.length} matched${lastRefreshed ? ` · synced ${formatDate(lastRefreshed) ?? ''}` : ''}`
      : 'Awaiting roll call — not yet filled'

  const titleRow = (
    <HStack>
      <Text fontWeight="bold">{ask.billNumber ? `${ask.billNumber} — ` : ''}{ask.title}</Text>
      <Badge colorPalette="purple">{ask.position}</Badge>
    </HStack>
  )

  const billHeader = ask.linkedBills.length > 0 && (
    <VStack align="stretch" gap={2} mt={3}>
      {ask.linkedBills.map((bill) => (
        <Box key={bill.id}>
          <HStack gap={2} flexWrap="wrap">
            <Text fontWeight="semibold" fontSize="sm">{bill.billNumber}</Text>
            {!!bill.statusLabel && (
              <StatusChip variant={billStatusVariant(bill.statusLabel)}>{billChipText(bill)}</StatusChip>
            )}
          </HStack>
          {!!bill.nextEventLabel && (
            <Text fontSize="xs" color="muted" mt={1}>
              tentative: {bill.nextEventLabel}{formatDate(bill.nextEventDate) ? ` · ${formatDate(bill.nextEventDate)}` : ''}
            </Text>
          )}
          <BillContentDetails summary={bill.summary} fullTextUrl={bill.fullTextUrl} />
        </Box>
      ))}
      <LegiScanCredit />
    </VStack>
  )

  // Always surfaced (LW-23): a no-bills cue with an inline "Link bills" affordance when nothing
  // is linked yet, else the existing bill header + derived roll-call status alongside Fill votes.
  const rollCallZone = rollCallState === 'none' ? (
    <>
      <HStack justify="space-between" flexWrap="wrap" gap={2} mt={3}>
        <Text fontSize="xs" color="muted">No bills linked — link a bill to pull the official roll call</Text>
        <Button size="sm" variant="ghost" onClick={() => setLinkOpen(true)}>
          <FiLink /> Link bills
        </Button>
      </HStack>
      <BillLinkModal
        eventId={eventId}
        askId={ask.askId}
        open={linkOpen}
        onClose={() => {
          setLinkOpen(false)
          qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
        }}
      />
    </>
  ) : (
    <>
      {billHeader}
      <HStack justify="space-between" flexWrap="wrap" gap={2} mt={3}>
        <HStack gap={2} flexWrap="wrap">
          <Text fontSize="xs" color="muted">{fillStatusText}</Text>
          {rollCallState === 'ingested' && <StatusChip variant="success">Ingested</StatusChip>}
        </HStack>
        <Button size="sm" colorPalette="brand" onClick={() => { void fill() }} loading={isPending}>
          <FiRefreshCw /> Fill votes from LegiScan
        </Button>
      </HStack>
    </>
  )

  const resultModal = (
    <ResponsiveModal isOpen={showResult} onClose={() => setShowResult(false)} size="md" isCentered scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Fill results</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            {!!result && <FillResultSummary result={result} askTitle={ask.title} />}
          </Dialog.Body>
          <Dialog.Footer>
            <Button colorPalette="brand" onClick={() => setShowResult(false)}>Done</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )

  if (ask.legislators.length === 0) {
    return (
      <Box bg="surface" p={4} borderRadius="lg" shadow="md">
        {titleRow}
        {rollCallZone}
        <Text mt={3} fontSize="sm" color="gray.500">No meetings scheduled for this ask yet.</Text>
        {resultModal}
      </Box>
    )
  }

  return (
    <Box bg="surface" p={4} borderRadius="lg" shadow="md">
      {titleRow}
      {rollCallZone}
      {isMobile ? (
        <VStack align="stretch" gap={3} mt={3}>
          {ask.legislators.map((row) => (
            <LegislatorVoteRow key={row.legislatorId} row={row} position={ask.position}
              missReason={missByName.get(row.legislatorName)}
              onChange={(v, o, n) => onRowChange(ask.askId, row.legislatorId, v, o, n)} />
          ))}
        </VStack>
      ) : (
        <Table.Root size="sm" mt={3}>
          <Table.Header bg="gray.50">
            <Table.Row><Table.ColumnHeader>Legislator</Table.ColumnHeader><Table.ColumnHeader>Promise</Table.ColumnHeader><Table.ColumnHeader>Actual vote</Table.ColumnHeader><Table.ColumnHeader>Disposition</Table.ColumnHeader><Table.ColumnHeader textAlign='end'>Attendees</Table.ColumnHeader><Table.ColumnHeader>Notes</Table.ColumnHeader></Table.Row>
          </Table.Header>
          <Table.Body>
            {ask.legislators.map((row) => (
              <LegislatorVoteRow key={row.legislatorId} row={row} position={ask.position}
                missReason={missByName.get(row.legislatorName)}
                onChange={(v, o, n) => onRowChange(ask.askId, row.legislatorId, v, o, n)} />
            ))}
          </Table.Body>
        </Table.Root>
      )}
      {resultModal}
    </Box>
  );
}
